import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@shared/design-system/ui'
import { formatMoney } from '@shared/lib/format/currency'
import type { AdminSaleRefundTarget } from '../lib/admin-sale-refund'
import {
  actions,
  auditNote,
  backdrop,
  body,
  debtAlert,
  field,
  header,
  help,
  irreversible,
  label,
  panel,
  scopeNote,
  summary,
  summaryItem,
  summaryTerm,
  summaryValue,
  textarea,
  title,
  total,
  totalLabel,
  totalValue,
} from './AdminSaleRefundDialog.css'

type AdminSaleRefundDialogProps = {
  target: AdminSaleRefundTarget | null
  buyer: string
  journeyChargeCount: number
  reason: string
  loading: boolean
  onReasonChange: (reason: string) => void
  onCancel: () => void
  onConfirm: () => void
}

export function AdminSaleRefundDialog({
  target,
  buyer,
  journeyChargeCount,
  reason,
  loading,
  onReasonChange,
  onCancel,
  onConfirm,
}: AdminSaleRefundDialogProps) {
  const panelRef = useRef<HTMLElement>(null)
  const reasonRef = useRef<HTMLTextAreaElement>(null)
  const loadingRef = useRef(loading)
  const onCancelRef = useRef(onCancel)
  const trimmedReason = reason.trim()
  const reasonInvalid = trimmedReason.length > 0 && trimmedReason.length < 3

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  useEffect(() => {
    onCancelRef.current = onCancel
  }, [onCancel])

  useEffect(() => {
    if (!target) return
    const previousActive =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    reasonRef.current?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !loadingRef.current) {
        onCancelRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.tabIndex >= 0)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (!panelRef.current?.contains(active)) {
        event.preventDefault()
        first?.focus()
      } else if (event.shiftKey && active === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      previousActive?.focus()
    }
  }, [target])

  if (!target) return null

  return createPortal(
    <div
      className={backdrop}
      role="presentation"
      onClick={() => {
        if (!loading) onCancel()
      }}
    >
      <section
        className={panel}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Reembolsar esta cobrança?"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={header}>
          <div>
            <h2 className={title}>Reembolsar esta cobrança?</h2>
            <p className={totalLabel}>
              {target.hasPartialRefund
                ? 'Reembolso do valor restante desta cobrança.'
                : 'Reembolso total desta cobrança, sem reembolso parcial.'}
            </p>
          </div>
          <div className={total}>
            <span className={totalLabel}>
              {target.hasPartialRefund
                ? 'Restante a devolver ao comprador'
                : 'A devolver ao comprador'}
            </span>
            <span className={totalValue}>
              {formatMoney(target.refundAmount.amount, target.refundAmount.currency)}
            </span>
          </div>
        </header>

        <div className={body}>
          <dl className={summary}>
            {target.settlementImpact &&
            target.settlementImpact.currency !== target.refundAmount.currency ? (
              <div className={summaryItem}>
                <dt className={summaryTerm}>Base original de responsabilidade</dt>
                <dd className={summaryValue}>
                  {formatMoney(target.settlementImpact.amount, target.settlementImpact.currency)}
                </dd>
              </div>
            ) : null}
            <div className={summaryItem}>
              <dt className={summaryTerm}>Cobrança</dt>
              <dd className={summaryValue}>{target.label}</dd>
            </div>
            <div className={summaryItem}>
              <dt className={summaryTerm}>Produto</dt>
              <dd className={summaryValue}>{target.productName}</dd>
            </div>
            <div className={summaryItem}>
              <dt className={summaryTerm}>Comprador</dt>
              <dd className={summaryValue}>{buyer}</dd>
            </div>
          </dl>

          <label className={field}>
            <span className={label}>Motivo do reembolso (opcional)</span>
            <textarea
              className={textarea}
              ref={reasonRef}
              value={reason}
              maxLength={500}
              disabled={loading}
              aria-invalid={reasonInvalid}
              aria-describedby={reasonInvalid ? 'admin-refund-reason-help' : undefined}
              onChange={(event) => onReasonChange(event.target.value)}
            />
            {reasonInvalid ? (
              <span className={help} id="admin-refund-reason-help">
                Use pelo menos 3 caracteres ou deixe o motivo em branco.
              </span>
            ) : null}
          </label>

          {journeyChargeCount > 1 ? (
            <p className={scopeNote}>
              Este pedido reembolsa somente a cobrança selecionada ({target.label}).{' '}
              {journeyChargeCount === 2
                ? 'A outra cobrança continua inalterada.'
                : 'As outras cobranças continuam inalteradas.'}
            </p>
          ) : null}

          {target.sharedLineCount > 1 ? (
            <p className={scopeNote}>
              Produto principal e order bump compartilham esta cobrança. A VendePay permite apenas o
              reembolso total desta cobrança; o reembolso de itens separados ainda não está
              disponível na plataforma.
            </p>
          ) : null}

          <div className={debtAlert} role="alert">
            Reembolsar pode gerar dívida para participantes quando valores já foram liberados ou
            sacados. A compensação ocorre nas próximas vendas.
          </div>
          <p className={auditNote}>A solicitação ficará registrada na auditoria administrativa.</p>
          <p className={irreversible}>Esta ação não pode ser desfeita.</p>
        </div>

        <footer className={actions}>
          <Button variant="secondary" disabled={loading} onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={loading || reasonInvalid} onClick={onConfirm}>
            {loading ? 'Confirmando...' : 'Confirmar reembolso'}
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
